// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// TanStack Query hook for the operator-console alarm set. Owns the server state
// (fetch/cache/loading/error/refetch); the view controls that shape the derived
// tabs live in stores/alarmsViewStore.ts. Reads alarmsService (seed-backed today,
// backend later). One query returns a site's full alarm set; a RQ `select`
// derives the per-tab lists + counts once per fetch (not per render), and
// `keepPreviousData` avoids a skeleton flash when the site changes.

import { useCallback } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { getAlarms } from '../services/alarmsService';
import { queryKeys } from '../lib/queryKeys';
import {
  selectTabAlarms,
  selectTabCounts,
  ALARM_TABS,
  type AlarmTabKey,
} from '../services/alarmSelectors';
import type { Alarm } from '../types/alarm';

export interface ConsoleAlarmsOptions {
  /**
   * Poll interval in ms. Opt-in (same shape as useDigitalTwin): omitting it
   * leaves the query un-polled, so a caller that only reads a count badge does
   * not silently start a timer. The app-wide driver that does set it is
   * hooks/useAlarmsAutoRefresh.ts.
   */
  pollIntervalMs?: number;
  /**
   * Fetch again on mount even when the cache is fresh. A page that draws alarm
   * state onto a diagram (Power Flow) asks for it, so it never opens on a list
   * another screen fetched up to a poll interval ago.
   */
  refetchOnMount?: 'always';
}

export interface ConsoleAlarmsData {
  /** Full site-scoped alarm set (all kinds). */
  alarms: Alarm[];
  /** Per-tab count badges. */
  counts: Record<AlarmTabKey, number | null>;
  /** Per-tab alarm lists (pre group-filter / sort). */
  byTab: Record<AlarmTabKey, Alarm[]>;
}

/** Derive the console view-model from the raw alarm set (runs inside RQ select). */
export function buildConsoleData(alarms: Alarm[], siteId?: string): ConsoleAlarmsData {
  const byTab = ALARM_TABS.reduce(
    (acc, t) => {
      acc[t.key] = selectTabAlarms(alarms, t.key, siteId);
      return acc;
    },
    {} as Record<AlarmTabKey, Alarm[]>
  );
  return { alarms, byTab, counts: selectTabCounts(alarms, siteId) };
}

/**
 * Fetch + derive the operator-console alarms for a site. Returns the full
 * TanStack Query result; `query.data` is the derived {@link ConsoleAlarmsData}.
 */
export function useConsoleAlarms(
  siteId?: string,
  { pollIntervalMs, refetchOnMount }: ConsoleAlarmsOptions = {}
) {
  // Stable per-site select identity so RQ memoizes the derivation.
  const select = useCallback((alarms: Alarm[]) => buildConsoleData(alarms, siteId), [siteId]);

  return useQuery({
    queryKey: queryKeys.consoleAlarms(siteId),
    queryFn: () => getAlarms({ siteId }),
    placeholderData: keepPreviousData,
    // Per-observer, so a poll only runs while something asked for one. The
    // client sets refetchIntervalInBackground: false, so a hidden tab is quiet.
    refetchInterval: pollIntervalMs ?? false,
    refetchOnMount,
    select,
  });
}
